import { useQuery } from "@apollo/client";
import { PersonAddAlt1 } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { type ReactNode, useState } from "react";
import { Title, useTranslate } from "react-admin";

import { RequirePermission } from "../components/RequirePermission";
import { InviteStaffDialog } from "../components/team/InviteStaffDialog";
import { StaffInvitationsTable } from "../components/team/StaffInvitationsTable";
import { StaffMembersTable } from "../components/team/StaffMembersTable";
import { ResourceName } from "../constants/resources";
import {
  StaffInvitationsDocument,
  type StaffInvitationsQuery,
  StaffMembersDocument,
  type StaffMembersQuery,
} from "../generated/graphql";
import { typography } from "../theme/designTokens";

const Team = () => {
  const translate = useTranslate();
  const { data, loading, error } = useQuery<StaffMembersQuery>(StaffMembersDocument, {
    fetchPolicy: "cache-and-network",
  });
  const members = data?.staffMembers;
  const invitationsQuery = useQuery<StaffInvitationsQuery>(StaffInvitationsDocument, {
    fetchPolicy: "cache-and-network",
  });
  const invitations = invitationsQuery.data?.staffInvitations;
  const [inviteOpen, setInviteOpen] = useState(false);

  let content: ReactNode = null;
  if (error) content = <Alert severity="error">{translate("team.loadError")}</Alert>;
  else if (loading && !members) content = <CircularProgress aria-label={translate("team.loading")} />;
  else if (members) content = <StaffMembersTable members={members} />;

  let invitationsContent: ReactNode = null;
  if (invitationsQuery.error) {
    invitationsContent = <Alert severity="error">{translate("team.invitations.loadError")}</Alert>;
  } else if (invitationsQuery.loading && !invitations) {
    invitationsContent = <CircularProgress aria-label={translate("team.invitations.loading")} />;
  } else if (invitations) {
    invitationsContent = <StaffInvitationsTable invitations={invitations} />;
  }

  return (
    <Stack gap={3} maxWidth={960} data-testid="team-page">
      <Title title={translate("team.title")} />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "flex-start" }}
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={typography.fontWeight.semibold}>
            {translate("team.heading")}
          </Typography>
          <Typography color="text.secondary">{translate("team.description")}</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddAlt1 />}
          onClick={() => setInviteOpen(true)}
          data-testid="invite-staff-button"
        >
          {translate("team.invite.open")}
        </Button>
      </Stack>
      {content}
      <Stack gap={2}>
        <Typography variant="h6">{translate("team.invitations.title")}</Typography>
        {invitationsContent}
      </Stack>
      <InviteStaffDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </Stack>
  );
};

export const TeamPage = () => (
  <RequirePermission permission={ResourceName.Staff}>
    <Team />
  </RequirePermission>
);
